// src/Components/EmailModal.jsx
import React, { useState } from 'react';
import './EmailModal.css';

const EmailModal = ({ show, onClose, locationData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (process.env.NODE_ENV === 'development') {
      console.log('Form submission started');
    }
    setIsSubmitting(true);
    setError(null);
    
    const formData = new FormData(e.target);
    
    // Debug: Log form data in development only
    if (process.env.NODE_ENV === 'development') {
      console.log('Form fields:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
    }
    
    // Add location information to form data
    if (locationData) {
      formData.append('signup_city', locationData.city);
      formData.append('signup_club', locationData.clubName);
      formData.append('signup_url', locationData.currentUrl);
      formData.append('signup_source', 'club_profile');
      if (process.env.NODE_ENV === 'development') {
        console.log('Added location data:', locationData);
      }
    }
    
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Sending request to getform.io...');
      }
      
      // Add timeout and more detailed error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('https://getform.io/f/bzyngyla', {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries([...response.headers.entries()])
        });
      }
      
      if (response.ok) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Form submitted successfully');
        }
        // Store in localStorage so modal doesn't show again
        localStorage.setItem('userEmailSubmitted', 'true');
        onClose();
      } else {
        const errorText = await response.text();
        if (process.env.NODE_ENV === 'development') {
          console.error('Server error response:', errorText);
        }
        setError(`Server error: ${response.status} ${response.statusText}. Please try again.`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Detailed error:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      
      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else if (error.message.includes('Failed to fetch')) {
        setError('Network connection failed. This might be a CORS issue in development.');
      } else {
        setError(`Network error: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Temporary skip function for development/testing
  const handleSkip = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Skipping email modal for development');
    }
    localStorage.setItem('userEmailSubmitted', 'true');
    onClose();
  };

  return (
    <div className="email-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="email-modal-content">
        <button className="email-modal-back-btn" onClick={handleGoBack} type="button">
          ← Back
        </button>
        <h2 style={{ marginTop: '10px' }}>Join the Clubsta Community!</h2>
        <p style={{ marginTop: '0px' }}>Enter your details to view this club profile</p>
        {error && (
          <div style={{ 
            color: '#d32f2f', 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            backgroundColor: '#ffebee', 
            borderRadius: '4px',
            fontSize: '14px',
            border: '1px solid #ffcdd2'
          }}>
            {error}
            {/* Temporary development bypass */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '0.5rem' }}>
                <button 
                  type="button" 
                  onClick={handleSkip}
                  style={{
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Skip for now (Dev only)
                </button>
              </div>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            name="first-name"
            placeholder="First Name"
            required
            disabled={isSubmitting}
            maxLength="50"
            pattern="[A-Za-z\s]+"
          />
          <input
            type="text"
            name="last-name"
            placeholder="Last Name"
            required
            disabled={isSubmitting}
            maxLength="50"
            pattern="[A-Za-z\s]+"
          />
          <input
            type="email"
            name="email"
            placeholder="Your Email"
            required
            disabled={isSubmitting}
            maxLength="100"
          />
          <p className="terms-text">
            By continuing, you accept our{' '}
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="terms-link"
            >
              Terms and Conditions.
            </a>{' '}
            By joining Clubsta, you agree that your information may be shared with this club so they can contact you. 
          </p>
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmailModal;